import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import {
  BoardOpinionState,
  BoardOpinionValue,
  type BoardOpinionRole,
  type ProjectGovernanceRequestDto,
} from '@paladin-monorepo/common';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { projectGovernanceApi } from '@/lib/api/project-governance';
import { useGovernanceMutation } from './useGovernanceMutation';

const COMMENT_MAX = 8000;

/** A request to the Project Board: summary, stale state, evidence changes, and Board opinions. */
export function GovernanceRequestCard({
  projectId,
  request,
  opinionRoles,
  nameOf,
  showSnapshot = false,
}: {
  projectId: string;
  request: ProjectGovernanceRequestDto;
  /** Roles in which the current user can record an opinion. */
  opinionRoles: BoardOpinionRole[];
  nameOf: (userId: string | null) => string;
  showSnapshot?: boolean;
}) {
  const { t } = useTranslation();
  const { snapshot } = request;

  return (
    <div className="space-y-3 rounded-md border border-border p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium">{t(`workspace.governance.request.kind.${request.kind}`)}</span>
        {request.stale && <Badge variant="destructive">{t('workspace.governance.request.stale')}</Badge>}
        <span className="text-xs text-muted-foreground">
          {t('workspace.governance.request.submittedBy', {
            name: nameOf(request.submittedByUserId),
            date: new Date(request.createdAt).toLocaleString(),
          })}
        </span>
      </div>

      {request.stale && (
        <p className="text-xs text-destructive">{t('workspace.governance.request.staleDescription')}</p>
      )}

      <div>
        <div className="text-xs text-muted-foreground">{t('workspace.governance.request.summary')}</div>
        <p className="whitespace-pre-wrap">{request.summary || t('workspace.governance.request.noSummary')}</p>
      </div>

      {request.executiveBrief && (
        <div className="rounded-md bg-muted/40 p-2 text-sm whitespace-pre-wrap">{request.executiveBrief}</div>
      )}

      {request.evidenceChanges.length > 0 && (
        <div className="rounded-md bg-muted/40 p-2">
          <div className="text-xs font-medium">{t('workspace.governance.request.evidenceChanged')}</div>
          <ul className="mt-1 space-y-0.5 text-xs">
            {request.evidenceChanges.map((c) => (
              <li key={c.key}>
                {evidenceLabel(c.key, snapshot, t)}: {String(c.before ?? '–')} → {String(c.after ?? '–')}
              </li>
            ))}
          </ul>
        </div>
      )}

      {snapshot.exceptionReport && (
        <div className="space-y-1 rounded-md bg-muted/40 p-2 text-xs">
          <div className="font-medium">{t('workspace.governance.exceptionReport.title')}</div>
          <p>
            {t('workspace.governance.exceptionReport.scope')}:{' '}
            {t(`workspace.governance.exceptionReport.scopeValue.${snapshot.exceptionReport.scope}`)}
          </p>
          <p>
            {t('workspace.governance.exceptionReport.cause')}: {snapshot.exceptionReport.cause}
          </p>
          <p>
            {t('workspace.governance.exceptionReport.impact')}: {snapshot.exceptionReport.impact}
          </p>
          <p>
            {t('workspace.governance.exceptionReport.recommendation')}: {snapshot.exceptionReport.recommendation}
          </p>
        </div>
      )}

      {showSnapshot && (
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <div className="text-xs text-muted-foreground">{t('workspace.governance.stages.title')}</div>
            <ul className="text-xs">
              {snapshot.stages.map((s) => (
                <li key={s.id}>
                  {s.title} · {t(`workspace.governance.stageStatus.${s.status}`)}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <div className="text-xs text-muted-foreground">{t('workspace.governance.milestones.sectionTitle')}</div>
            <ul className="text-xs">
              {snapshot.milestones.map((m) => (
                <li key={m.id}>{m.title}</li>
              ))}
            </ul>
          </div>
          {snapshot.financial && typeof snapshot.financial.plannedBudget === 'number' && (
            <div className="text-xs">
              {t('workspace.governance.request.plannedBudget')}: {snapshot.financial.plannedBudget.toLocaleString()}{' '}
              {String(snapshot.financial.currency ?? '')}
            </div>
          )}
        </div>
      )}

      {request.opinions.length > 0 && (
        <div className="space-y-1">
          <div className="text-xs text-muted-foreground">{t('workspace.governance.request.opinions')}</div>
          <ul className="space-y-1">
            {request.opinions.map((o) => (
              <li key={o.role} className="flex flex-wrap items-center gap-2">
                <span>{t(`workspace.governance.owner.${o.role}`)}</span>
                <span className="text-xs text-muted-foreground">{nameOf(o.userId)}</span>
                <Badge variant={opinionVariant(o.state, o.opinion)}>
                  {o.state === BoardOpinionState.Given && o.opinion
                    ? t(`workspace.governance.request.opinionValue.${o.opinion}`)
                    : t(`workspace.governance.request.opinionState.${o.state}`)}
                </Badge>
                {o.comment && <span className="w-full text-xs text-muted-foreground">{o.comment}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {!request.stale &&
        opinionRoles.map((role) => (
          <OpinionForm key={role} projectId={projectId} requestId={request.id} role={role} />
        ))}
    </div>
  );
}

function evidenceLabel(
  key: string,
  snapshot: ProjectGovernanceRequestDto['snapshot'],
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const milestone = /^milestone\.(\d+)\.status$/.exec(key);
  if (milestone) {
    const title = snapshot.milestones.find((m) => String(m.id) === milestone[1])?.title ?? milestone[1];
    return t('workspace.governance.request.evidence.milestoneStatus', { title });
  }
  const tolerance = /^tolerance\.(PROJECT|STAGE\.[^.]+)\.([A-Z_]+)$/.exec(key);
  if (tolerance) {
    const level = tolerance[1].startsWith('STAGE.') ? 'stage' : 'project';
    return t('workspace.governance.request.evidence.toleranceStatus', {
      area: tolerance[2],
      level,
    });
  }
  return t(`workspace.governance.request.evidence.${key}`, { defaultValue: key });
}

function opinionVariant(state: BoardOpinionState, opinion: BoardOpinionValue | null) {
  if (state !== BoardOpinionState.Given) return 'outline' as const;
  return opinion === BoardOpinionValue.Object ? ('destructive' as const) : ('success' as const);
}

function OpinionForm({ projectId, requestId, role }: { projectId: string; requestId: string; role: BoardOpinionRole }) {
  const { t } = useTranslation();
  const schema = yup.object({ comment: yup.string().max(COMMENT_MAX).defined() }).required();
  const { register, handleSubmit, reset } = useForm({
    resolver: yupResolver(schema),
    defaultValues: { comment: '' },
  });
  const mutation = useGovernanceMutation(
    projectId,
    (v: { opinion: BoardOpinionValue; comment: string }) =>
      projectGovernanceApi.recordOpinion(projectId, requestId, {
        role,
        opinion: v.opinion,
        comment: v.comment.trim() || null,
      }),
    () => reset(),
  );
  const record = (opinion: BoardOpinionValue) =>
    handleSubmit((v) => mutation.mutate({ opinion, comment: v.comment }));
  const fieldId = `opinion-${requestId}-${role}`;

  return (
    <form className="space-y-2 border-t pt-3" onSubmit={(e) => e.preventDefault()}>
      <Label htmlFor={fieldId}>
        {t('workspace.governance.request.myOpinion', { role: t(`workspace.governance.owner.${role}`) })}
      </Label>
      <Textarea
        id={fieldId}
        rows={2}
        maxLength={COMMENT_MAX}
        placeholder={t('workspace.governance.request.comment')}
        {...register('comment')}
      />
      <div className="flex gap-2">
        <Button size="sm" onClick={record(BoardOpinionValue.Agree)} disabled={mutation.isPending}>
          {t('workspace.governance.request.agree')}
        </Button>
        <Button
          size="sm"
          variant="outline"
          onClick={record(BoardOpinionValue.Object)}
          disabled={mutation.isPending}
        >
          {t('workspace.governance.request.object')}
        </Button>
      </div>
    </form>
  );
}
